import type { ProviderConfig } from '@deltasuite/auth';

// Reads the Vite env at module load. The dev fallback path (no API URL +
// X-Dev-User-Id header in api/client.ts) doesn't need a real provider — we
// still mount AuthProvider so useAuth() is callable everywhere, but with
// the dormant Entra provider so no Supabase round-trips happen locally.

// Important: use `||` instead of `??` for the defaults below.
// Cloudflare Pages (and some other build hosts) expose unset env vars as
// empty strings rather than undefined when the variable name appears in
// the dashboard with no value. `??` only coalesces nullish — empty string
// passes through. `||` coalesces any falsy, which is what we want here:
// "" / undefined / whitespace should all fall back to the default.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim() || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || '';
const redirectUri =
  import.meta.env.VITE_AUTH_REDIRECT_URI?.trim() ||
  (typeof window !== 'undefined' ? window.location.origin : '');
const providerRaw = import.meta.env.VITE_AUTH_PROVIDER?.trim() || 'supabase';
const provider: 'supabase' | 'entra' = providerRaw === 'entra' ? 'entra' : 'supabase';

export const isAuthConfigured =
  provider === 'supabase' && supabaseUrl.length > 0 && supabaseAnonKey.length > 0;

/** Returns the list of env-var names that need values for a working prod
 * build. Used by <AuthMisconfigured/>. VITE_AUTH_REDIRECT_URI is omitted
 * from the required set — it falls back to window.location.origin at
 * runtime, which is the right default for Cloudflare Pages preview URLs.
 */
export function missingProdEnvVars(): readonly string[] {
  // Accept either VITE_DIAL_API_URL or the older VITE_API_BASE_URL — the
  // api/client.ts fetch wrapper checks both so we mirror that here.
  const dialApiUrl =
    (import.meta.env.VITE_DIAL_API_URL?.trim() || '').length > 0 ||
    (import.meta.env.VITE_API_BASE_URL?.trim() || '').length > 0;
  const missing: string[] = [];
  if (supabaseUrl.length === 0) missing.push('VITE_SUPABASE_URL');
  if (supabaseAnonKey.length === 0) missing.push('VITE_SUPABASE_ANON_KEY');
  if (!dialApiUrl) missing.push('VITE_DIAL_API_URL or VITE_API_BASE_URL');
  return missing;
}

export function buildAuthConfig(): ProviderConfig {
  if (provider === 'entra') {
    return {
      kind: 'entra',
      config: {
        tenantId: import.meta.env.VITE_AUTH_TENANT_ID ?? '',
        clientId: import.meta.env.VITE_AUTH_CLIENT_ID ?? '',
        redirectUri,
        allowedDomain: 'zanda.com.au',
      },
    };
  }
  return {
    kind: 'supabase',
    config: {
      supabaseUrl,
      supabaseAnonKey,
      redirectUri,
      allowedDomain: 'zanda.com.au',
    },
  };
}
